import { type FieldValues } from 'react-hook-form';
import isPlainObject from 'lodash.isplainobject';

import { type TOptionWithTwoDescriptions } from '../../../components/OptionWithTwoDescriptions/OptionWithTwoDescriptions';
import { type IAttachmentFormValues } from '../components/Attachments/types';
import { type ISavedItem } from '../components/Items/types';

import { formatDateForBackend } from '../../../helpers/dateHelpers';

function getSelectValue<T extends SelectOption>(value: T | null | undefined) {
  return value?.value ?? null;
}

function getSelectTagLabel<T extends SelectOption>(value: T | null | undefined) {
  return value?.tag?.label ?? null;
}

function getSelectDescription<T extends SelectOption>(value: T | null | undefined) {
  return (value?.label as { title: string; description: string })?.description ?? null;
}

function getAdditionalFieldsExportData(
  additionalFieldValues: Record<string, string | Date | number | SelectOption | null>
) {
  return Object.entries(additionalFieldValues).map(([key, value]) => ({
    fieldId: key.slice(3),
    fieldValue:
      value instanceof Date
        ? formatDateForBackend(value)
        : isPlainObject(value)
        ? (value as SelectOption)?.value
        : value,
  }));
}

function getDbOptionAdditionalFieldsExportData(
  dbOptionAdditionalFieldValues: Record<string, TOptionWithTwoDescriptions>
) {
  return Object.entries(dbOptionAdditionalFieldValues).map(([key, value]) => ({
    fieldId: key.slice(3),
    fieldCode: value?.label ?? null,
    fieldValue1: value?.name ?? null,
    fieldValue2: value?.name2 ?? null,
    optionId: value?.value ?? null,
  }));
}

function getItemExportData(items: ISavedItem[]) {
  return items.map((item: ISavedItem) => {
    const {
      id,
      additionalFields,
      dbOptionAdditionalFields,
      requestedUomCode,
      approvedUomCode,
      commodityCode,
      countryOfOriginCode,
      packageTypeCode,
      treatments,
      packages,
      ...rest
    } = item;

    return {
      ...rest,
      requestedUomCode: getSelectValue(requestedUomCode),
      requestedUomName: getSelectDescription(requestedUomCode),
      approvedUomCode: getSelectValue(approvedUomCode),
      approvedUomName: getSelectDescription(approvedUomCode),
      commodityCode: getSelectValue(commodityCode),
      countryOfOriginCode: getSelectValue(countryOfOriginCode),
      countryOfOriginName: getSelectTagLabel(countryOfOriginCode),
      packageTypeCode: getSelectValue(packageTypeCode),
      packageTypeName: getSelectTagLabel(packageTypeCode),
      additionalFields: getAdditionalFieldsExportData(additionalFields),
      dbOptionAdditionalFields: getDbOptionAdditionalFieldsExportData(dbOptionAdditionalFields),
      packages: packages?.map(({ id, ...rest }) => rest) ?? [],
      treatments: treatments?.map(({ id, ...rest }) => rest) ?? [],
    };
  });
}

function getAttachmentExportData(attachedDocuments: IAttachmentFormValues[]) {
  return attachedDocuments.map((attachedDocument) => {
    const { id, code, fileUrl, parentAttachmentId, ...rest } = attachedDocument;

    return {
      ...rest,
      code: getSelectValue(code),
      name: getSelectTagLabel(code),
      fileUrl: null,
    };
  });
}

function convertFormDataForExport(values: FieldValues) {
  const {
    id,
    items,
    attachedDocuments,
    beneficiaries,
    fees,
    additionalFields,
    dbOptionAdditionalFields,
    transactionRecords,
    companyCode,
    countryOfDestinationCode,
    customsOfficeCode,
    departmentOfficeCode,
    invoiceCurrencyCode,
    licenseType,
    placeOfLoadingCode,
    placeOfUnloadingCode,
    termsOfDeliveryCode,
    typeOfUse,
    transitCountryCode,
    meanOfTransportCode,
    ...rest
  } = values;

  return {
    ...rest,
    licenseType: getSelectValue(licenseType),
    companyCode: getSelectValue(companyCode),
    companyDescription: getSelectTagLabel(companyCode),
    countryOfDestinationCode: getSelectValue(countryOfDestinationCode),
    countryOfDestinationName: getSelectTagLabel(countryOfDestinationCode),
    customsOfficeCode: getSelectValue(customsOfficeCode),
    customsOfficeName: getSelectTagLabel(customsOfficeCode),
    departmentOfficeCode: getSelectValue(departmentOfficeCode),
    departmentOfficeName: getSelectTagLabel(departmentOfficeCode),
    invoiceCurrencyCode: getSelectValue(invoiceCurrencyCode),
    invoiceExchangeRate: getSelectTagLabel(invoiceCurrencyCode),
    placeOfLoadingCode: getSelectValue(placeOfLoadingCode),
    placeOfLoadingName: getSelectTagLabel(placeOfLoadingCode),
    placeOfUnloadingCode: getSelectValue(placeOfUnloadingCode),
    placeOfUnloadingName: getSelectTagLabel(placeOfUnloadingCode),
    termsOfDeliveryCode: getSelectValue(termsOfDeliveryCode),
    termsOfDeliveryDescription: getSelectTagLabel(termsOfDeliveryCode),
    typeOfUse: getSelectValue(typeOfUse),
    transitCountryCode: getSelectValue(transitCountryCode),
    transitCountryName: getSelectTagLabel(transitCountryCode),
    meanOfTransportCode: getSelectValue(meanOfTransportCode),
    meanOfTransportName: getSelectTagLabel(meanOfTransportCode),

    items: getItemExportData(items as ISavedItem[]),
    attachedDocuments: getAttachmentExportData(attachedDocuments as IAttachmentFormValues[]),
    additionalFields: getAdditionalFieldsExportData(
      additionalFields as Record<string, string | Date | number | SelectOption>
    ),
    dbOptionAdditionalFields: getDbOptionAdditionalFieldsExportData(
      dbOptionAdditionalFields as Record<string, TOptionWithTwoDescriptions>
    ),
  };
}

export default convertFormDataForExport;
